import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Search, TrendingDown, TrendingUp, Minus, RefreshCw } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  DataTable,
  EmptyState,
  LoadingState,
  ErrorState,
  PageHeader,
  Card,
  IconButton,
  FilterBar,
  FilterChip,
  type ColumnDef,
} from '@/components/ui'
import { useToast } from '@/components/ui'
import { formatCurrency } from '../shared.ts'

// ─── Types ────────────────────────────────────────────────────────────────────

interface SupplierPriceRow {
  articleId: string
  itemCode: string
  itemName: string
  groupName: string | null
  manufacturer: string | null
  sapPurchasePriceCents: number
  egisProductId: number | null
  egisProductName: string | null
  egisManufacturerProductNumber: string | null
  egisPriceCents: number | null
  deltaPercent: number | null
  checkedAt: string | null
  status: 'cheaper' | 'expensive' | 'equal' | 'not_found' | 'unchecked'
  egisError: string | null
  egisSearchTerm: string | null
}

interface SupplierSummary {
  checkedCount: number
  cheaperCount: number
  expensiveCount: number
  uncheckedCount: number
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type StatusFilter = 'all' | 'cheaper' | 'expensive' | 'not_found' | 'unchecked'

function DeltaBadge({ deltaPercent, status }: { deltaPercent: number | null; status: SupplierPriceRow['status'] }) {
  if (status === 'unchecked') return <span className="text-gray-400 text-xs">Nicht geprüft</span>
  if (status === 'not_found') return <span className="text-gray-400 text-xs">Nicht gefunden</span>
  if (status === 'equal' || deltaPercent === null) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
      <Minus className="w-3 h-3" />= {deltaPercent !== null ? `${deltaPercent > 0 ? '+' : ''}${deltaPercent.toFixed(1)} %` : ''}
    </span>
  )
  if (deltaPercent < 0) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
      <TrendingDown className="w-3 h-3" />{deltaPercent.toFixed(1)} %
    </span>
  )
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
      <TrendingUp className="w-3 h-3" />+{deltaPercent.toFixed(1)} %
    </span>
  )
}

function formatCheckedAt(s: string | null) {
  if (!s) return '–'
  return new Date(s).toLocaleDateString('de-DE')
}

// ─── Section ──────────────────────────────────────────────────────────────────

export function SupplierPriceSection() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [lookingUp, setLookingUp] = useState<Set<string>>(new Set())
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: summary } = useQuery<SupplierSummary>({
    queryKey: ['supplier-prices-summary'],
    queryFn: () => apiClient.get('/articles/supplier-prices/summary').then((r) => r.data),
  })

  const params = new URLSearchParams()
  if (statusFilter !== 'all') params.set('status', statusFilter)

  const { data = [], isLoading, error } = useQuery<SupplierPriceRow[]>({
    queryKey: ['supplier-prices', statusFilter],
    queryFn: () => apiClient.get(`/articles/supplier-prices?${params}`).then((r) => r.data),
  })

  async function handleLookup(articleId: string, itemCode: string) {
    setLookingUp((prev) => new Set([...prev, articleId]))
    try {
      const { data: result } = await apiClient.post<SupplierPriceRow>(`/articles/supplier-prices/${articleId}/lookup`, {})
      await queryClient.invalidateQueries({ queryKey: ['supplier-prices'] })
      await queryClient.invalidateQueries({ queryKey: ['supplier-prices-summary'] })

      if (result.egisError) {
        toast({
          title: `EGIS-Fehler bei ${itemCode}`,
          description: result.egisError,
          tone: 'danger',
        })
      } else if (result.egisProductId === null) {
        const searchInfo = result.egisSearchTerm ? ` (Suche: „${result.egisSearchTerm}")` : ''
        toast({
          title: `Kein EGIS-Treffer für ${itemCode}`,
          description: `EGIS hat kein passendes Produkt gefunden.${searchInfo}`,
          tone: 'warning',
        })
      } else {
        toast({ title: `EGIS-Preis für ${itemCode} aktualisiert`, tone: 'success' })
      }
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, tone: 'danger' })
    } finally {
      setLookingUp((prev) => { const s = new Set(prev); s.delete(articleId); return s })
    }
  }

  const columns: ColumnDef<SupplierPriceRow>[] = [
    { id: 'itemCode', header: 'Art.-Nr.', accessorKey: 'itemCode', size: 110 },
    { id: 'itemName', header: 'Bezeichnung', accessorKey: 'itemName', size: 270 },
    { id: 'manufacturer', header: 'Hersteller', accessorKey: 'manufacturer', size: 130,
      cell: ({ row }) => row.original.manufacturer ?? <span className="text-gray-400 text-xs">–</span> },
    { id: 'sapPurchasePriceCents', header: 'SAP EK', accessorKey: 'sapPurchasePriceCents', size: 100,
      cell: ({ row }) => row.original.sapPurchasePriceCents > 0
        ? formatCurrency(row.original.sapPurchasePriceCents / 100)
        : <span className="text-gray-400 text-xs">–</span> },
    { id: 'egisPriceCents', header: 'EGIS EK (günstigster)', accessorKey: 'egisPriceCents', size: 150,
      cell: ({ row }) => row.original.egisPriceCents
        ? formatCurrency(row.original.egisPriceCents / 100)
        : <span className="text-gray-400 text-xs">–</span> },
    { id: 'deltaPercent', header: 'Differenz', accessorKey: 'deltaPercent', size: 130,
      cell: ({ row }) => <DeltaBadge deltaPercent={row.original.deltaPercent} status={row.original.status} /> },
    { id: 'egisProductName', header: 'EGIS Produkt', accessorKey: 'egisProductName', size: 200,
      cell: ({ row }) => (
        <span className="text-xs text-gray-600 truncate block max-w-[200px]" title={row.original.egisProductName ?? ''}>
          {row.original.egisProductName ?? <span className="text-gray-400">–</span>}
        </span>
      ),
    },
    { id: 'checkedAt', header: 'Geprüft am', accessorKey: 'checkedAt', size: 110,
      cell: ({ row }) => <span className="text-xs text-gray-500">{formatCheckedAt(row.original.checkedAt)}</span> },
    {
      id: 'actions', header: '', size: 50,
      cell: ({ row }) => (
        <IconButton
          icon={lookingUp.has(row.original.articleId)
            ? <RefreshCw className="w-4 h-4 animate-spin" />
            : <Search className="w-4 h-4" />}
          aria-label="EGIS-Preis abfragen"
          disabled={lookingUp.has(row.original.articleId)}
          onClick={() => handleLookup(row.original.articleId, row.original.itemCode)}
        />
      ),
    },
  ]

  if (isLoading) return <LoadingState label="Preisvergleich wird geladen…" />
  if (error) {
    const axiosErr = error as { message: string; response?: { data?: { error?: string; stack?: string } } }
    return (
      <ErrorState
        title="Fehler beim Laden des Preisvergleichs"
        description={axiosErr.response?.data?.error ?? axiosErr.message}
        details={axiosErr.response?.data?.stack}
      />
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Lieferanten-Preisvergleich"
        description="SAP-Einkaufspreise vs. EGIS-Aktuellpreise — klicke das Lupen-Symbol um einen Artikel live abzufragen"
      />

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <Card.Body className="p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{summary.checkedCount}</div>
              <div className="text-xs text-gray-500 mt-1">Geprüft</div>
            </Card.Body>
          </Card>
          <Card
            className={`cursor-pointer hover:ring-2 hover:ring-green-300 ${statusFilter === 'cheaper' ? 'ring-2 ring-green-400' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'cheaper' ? 'all' : 'cheaper')}
          >
            <Card.Body className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{summary.cheaperCount}</div>
              <div className="text-xs text-gray-500 mt-1">EGIS günstiger</div>
            </Card.Body>
          </Card>
          <Card
            className={`cursor-pointer hover:ring-2 hover:ring-red-300 ${statusFilter === 'expensive' ? 'ring-2 ring-red-400' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'expensive' ? 'all' : 'expensive')}
          >
            <Card.Body className="p-4 text-center">
              <div className="text-2xl font-bold text-red-600">{summary.expensiveCount}</div>
              <div className="text-xs text-gray-500 mt-1">SAP günstiger</div>
            </Card.Body>
          </Card>
          <Card
            className={`cursor-pointer hover:ring-2 hover:ring-gray-300 ${statusFilter === 'unchecked' ? 'ring-2 ring-gray-400' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'unchecked' ? 'all' : 'unchecked')}
          >
            <Card.Body className="p-4 text-center">
              <div className="text-2xl font-bold text-gray-500">{summary.uncheckedCount}</div>
              <div className="text-xs text-gray-500 mt-1">Noch nicht geprüft</div>
            </Card.Body>
          </Card>
        </div>
      )}

      <FilterBar>
        <FilterChip active={statusFilter === 'all'} onRemove={() => setStatusFilter('all')}>Alle</FilterChip>
        <FilterChip active={statusFilter === 'cheaper'} onRemove={() => setStatusFilter(statusFilter === 'cheaper' ? 'all' : 'cheaper')}>EGIS günstiger</FilterChip>
        <FilterChip active={statusFilter === 'expensive'} onRemove={() => setStatusFilter(statusFilter === 'expensive' ? 'all' : 'expensive')}>SAP günstiger</FilterChip>
        <FilterChip active={statusFilter === 'not_found'} onRemove={() => setStatusFilter(statusFilter === 'not_found' ? 'all' : 'not_found')}>Nicht gefunden</FilterChip>
        <FilterChip active={statusFilter === 'unchecked'} onRemove={() => setStatusFilter(statusFilter === 'unchecked' ? 'all' : 'unchecked')}>Ungeprüft</FilterChip>
      </FilterBar>

      {data.length === 0 ? (
        <EmptyState title="Keine Daten" description="Klicke das Lupen-Symbol bei einem Artikel in der Artikelliste um den EGIS-Preis abzufragen." />
      ) : (
        <DataTable columns={columns} data={data} pagination={{ pageSize: 50 }} />
      )}
    </div>
  )
}
